import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail, Download, Building2, Users } from "lucide-react";
import { SectionHeader } from "./press";
import { brandedTitle, safeDesc } from "@/lib/seo-meta";

const SITE = "https://heallexa.com";

const META_TITLE = brandedTitle("Press | Media & Newsroom");
const META_DESC = safeDesc(
  "Heallexa press room - company overview, founder bio, brand assets, and press contact. Reach out to press@heallexa.com for interviews and media inquiries."
);

export const Route = createFileRoute("/press/")({
  head: () => ({
    meta: [
      { title: META_TITLE },
      { name: "description", content: META_DESC },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESC },
      { property: "og:url", content: `${SITE}/press` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/press` }],
  }),
  component: OverviewPanel,
});

function OverviewPanel() {
  return (
    <div className="space-y-14">
      <section>
        <SectionHeader eyebrow="Company" title="About Heallexa" />
        <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
          Heallexa is an AI powered healthcare search platform that helps patients find the right
          provider by specialty, location, insurance, and real-time availability. Our mission is to
          make healthcare access faster, clearer, and more personal, using AI to surface trusted
          providers and help practices grow their online presence.
        </p>
      </section>

      <section>
        <SectionHeader eyebrow="Leadership" title="Founder" />
        <div className="mt-6 rounded-2xl border border-border bg-gradient-to-br from-card to-card/40 p-6 md:p-8">
          <div className="flex items-center gap-5">
            <img
              src="/founder-obrist-buasa.jpg"
              alt="Obrist Buasa"
              className="h-20 w-20 rounded-full object-cover ring-2 ring-primary/20 ring-offset-2 ring-offset-background"
              loading="lazy"
              decoding="async"
            />
            <div>
              <p className="text-xl font-semibold text-foreground tracking-tight">Obrist Buasa</p>
              <p className="text-sm text-primary font-medium">Founder, Heallexa</p>
            </div>
          </div>
          <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
            Read the founder's full story on the{" "}
            <Link to="/about" className="text-primary hover:underline font-medium">
              About page
            </Link>
            .
          </p>
        </div>
      </section>

      <section>
        <SectionHeader
          eyebrow="Downloads"
          title="Brand Assets"
          subtitle="Official Heallexa logo and imagery for editorial use."
        />
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <a
            href="/logo.png"
            download
            className="group flex items-center justify-between rounded-xl border border-border bg-card p-5 hover:border-primary/30 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/5 p-2 ring-1 ring-primary/10">
                <img src="/logo.png" alt="Heallexa logo" className="h-10 w-10 rounded" loading="lazy" decoding="async" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Heallexa Logo</p>
                <p className="text-xs text-muted-foreground">PNG, High resolution</p>
              </div>
            </div>
            <Download className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-all" />
          </a>
          <a
            href="/founder-obrist-buasa.jpg"
            download
            className="group flex items-center justify-between rounded-xl border border-border bg-card p-5 hover:border-primary/30 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-4">
              <img
                src="/founder-obrist-buasa.jpg"
                alt="Founder headshot"
                className="h-12 w-12 rounded-full object-cover ring-1 ring-border"
                loading="lazy"
                decoding="async"
              />
              <div>
                <p className="text-sm font-semibold text-foreground">Founder Headshot</p>
                <p className="text-xs text-muted-foreground">JPG, Obrist Buasa</p>
              </div>
            </div>
            <Download className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-all" />
          </a>
        </div>
      </section>

      <section id="contact" className="relative overflow-hidden rounded-2xl border border-primary/15 p-8 md:p-10">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/[0.08] via-card to-card" />
        <div className="absolute -top-20 -right-20 -z-10 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
        <SectionHeader
          eyebrow="Contact"
          title="Press Contact"
          subtitle="For interviews, quotes, or additional information, reach out to our press team."
        />
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <a
            href="mailto:press@heallexa.com"
            className="group flex items-center gap-4 rounded-xl border border-border bg-card p-5 hover:border-primary/30 hover:shadow-md transition-all"
          >
            <div className="rounded-lg bg-primary/10 p-2.5">
              <Mail className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Email
              </p>
              <p className="mt-0.5 text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                press@heallexa.com
              </p>
            </div>
          </a>
          <a
            href={SITE}
            className="group flex items-center gap-4 rounded-xl border border-border bg-card p-5 hover:border-primary/30 hover:shadow-md transition-all"
          >
            <div className="rounded-lg bg-primary/10 p-2.5">
              <Building2 className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Company
              </p>
              <p className="mt-0.5 text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                Heallexa, heallexa.com
              </p>
            </div>
          </a>
        </div>
        <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
          <Users className="h-3.5 w-3.5 text-primary" />
          Press inquiries answered within <span className="font-semibold text-foreground">24 hours</span>.
        </div>
      </section>
    </div>
  );
}
